import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PageShell, BackHeader, SectionTitle, KpiSection, KpiTile, KpiGrid, LockVeil, LoadMore, Icon, Skeleton } from '../../components';
import { ASSET_PREVIEW, ASSET_SKELETON, ASSET_CHANNEL_COLORS, COLORS } from '../../theme/tokens';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { Sheet } from '@/nova/components/Sheet';
import type { RunningCampaignData, CampaignAsset } from '../../mock-data/types';
import {
  Hero,
  HeroCard,
  Sheen,
  HeroMain,
  HeroThumb,
  HeroTitles,
  HeroTitle,
  HeroSubtitle,
  HeroOffering,
  AdSpend,
  AdSpendValue,
  AdSpendLabel,
  OfferingList,
  OfferingGroup,
  OfferingLabel,
  OfferingValue,
  HeroControls,
  CrossLink,
  CrossLinkLabel,
  PauseButton,
  PauseLabel,
  ResumeButton,
  ResumeLabel,
  EndedTag,
  EndedLabel,
  AssetsSection,
  AssetsPanel,
  AssetGrid,
  AssetCard,
  AssetThumb,
  AssetImg,
  ZoomImg,
  ZoomMeta,
  DimBadge,
  AssetInfo,
  AssetName,
  AssetMetaLine,
  AssetMetaType,
  AssetMetaChannels,
  WireBox,
  WireImg,
  WireLines,
  WireBar,
  WireBtn,
  WebsitesSection,
  WebsitesPanel,
  WebsitesEmpty,
  WebsitesList,
  WebsiteRow,
  Rank,
  RowLine,
  WebsiteName,
  Bullet,
  WebsiteValue,
  PerfSection,
  TileLock,
  type WireVariant,
  type HeroGlow,
} from './running-campaign.styles';

export type RunningCampaignProps = {
  data: RunningCampaignData;
  /**
   * Which state to render. `running` shows the live hero glow + Pause button;
   * `paused` drops the glow and swaps Pause for a primary "Resume" button;
   * `ended` shows the non-interactive "Ended on …" tag. Defaults to `running`.
   */
  status?: 'running' | 'paused' | 'ended';
  /**
   * The entity noun for the Pause/Resume button copy. This template is shared by
   * the acquisition campaign and operations promotion details, so the buttons
   * read "Pause/Resume Campaign" vs "Pause/Resume Promotion" accordingly.
   * Defaults to `campaign`.
   */
  kind?: 'campaign' | 'promotion';
  /** Pause a running campaign. */
  onPause?: () => void;
  /** Resume a paused campaign (the dispatcher enforces the expiry rule). */
  onResume?: () => void;
  /**
   * Real per-section loading (the wired acquisition campaign-detail path): `heroAdSpend`
   * gates the hero spend figure, `performance` the KPI grid, `topWebsites` the sites
   * list. When omitted the template simulates loading with its own mock endpoints
   * (operations promotions, still mock).
   */
  loading?: { heroAdSpend: boolean; performance: boolean; topWebsites: boolean };
  /**
   * Gate the Campaign Performance grid behind an "Unlocking soon" veil. Set by the
   * operations promotion detail (its per-promotion KPIs have no API source yet, so the
   * grid is still mock); the acquisition campaign detail leaves it off (its figures are
   * wired to Ad Service + Stats). The section title stays visible; only the KPI cards
   * are frosted. Reveals live via the `promotion-performance` flag / `show_in_progress`.
   */
  performanceLocked?: boolean;
  styleText?: string;
  className?: string;
};

const noop = (): void => undefined;

/** Filled play triangle for the Resume button — drawn inline (like
 * `ExternalLinkIcon`) so it tints to the primary button's on-accent text. */
const PlayIcon = ({ color }: { color: string }): React.ReactElement => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

/** Fit an ad format (w×h) into the 220×86 preview region, preserving aspect. */
const computeBox = (w: number, h: number): { w: number; h: number; variant: WireVariant } => {
  const scale = Math.min(220 / w, 86 / h);
  const bw = Math.max(26, Math.round(w * scale));
  const bh = Math.max(18, Math.round(h * scale));
  const variant: WireVariant = bw < 46 ? 'narrow' : bh >= 56 ? 'tall' : 'wide';
  return { w: bw, h: bh, variant };
};

/** The schematic wireframe inside an asset thumbnail — a dashed box sized to the
 * format's aspect ratio with a few skeleton blocks suggesting a creative. */
const AssetWire = ({ asset }: { asset: CampaignAsset }): React.ReactElement => {
  const tone = ASSET_PREVIEW[asset.tone];
  const box = computeBox(asset.width, asset.height);
  const common = { $w: box.w, $h: box.h, $border: tone.border, $fill: tone.fill, $variant: box.variant };
  if (box.variant === 'narrow') {
    return (
      <WireBox {...common}>
        <WireBar $color={ASSET_SKELETON.barStrong} $w="14px" $h="42%" />
        <WireBar $color={tone.button} $w="6px" $h="52%" />
      </WireBox>
    );
  }
  if (box.variant === 'tall') {
    return (
      <WireBox {...common}>
        <WireImg $color={tone.block} />
        <WireLines>
          <WireBar $color={ASSET_SKELETON.barStrong} $h="5px" />
          <WireBar $color={ASSET_SKELETON.barWeak} $w="70%" />
          <WireBar $color={ASSET_SKELETON.barWeak} $w="82%" />
          <WireBtn $color={tone.button} />
        </WireLines>
      </WireBox>
    );
  }
  return (
    <WireBox {...common}>
      <WireImg $color={tone.block} />
      <WireLines>
        <WireBar $color={ASSET_SKELETON.barStrong} $h="4px" />
        {box.h > 30 ? <WireBar $color={ASSET_SKELETON.barWeak} $w="68%" /> : null}
      </WireLines>
    </WireBox>
  );
};

/**
 * The thumbnail preview: the REAL generated banner image when the asset carries one
 * (a wired campaign), falling back to the schematic wireframe for the still-mock
 * ended / legacy samples, or if the banner image fails to load.
 */
const AssetPreview = ({ asset }: { asset: CampaignAsset }): React.ReactElement => {
  const [failed, setFailed] = React.useState(false);
  if (asset.image && !failed) {
    return <AssetImg src={asset.image} alt={asset.name} loading="lazy" onError={() => setFailed(true)} />;
  }
  return <AssetWire asset={asset} />;
};

/** Channel list colour, keyed off the lead platform (web/crypto blue, apps orange). */
const channelColor = (channels: string[]): string => ASSET_CHANNEL_COLORS[channels[0]] ?? ASSET_CHANNEL_COLORS.Web;

/** Outbound-link glyph — an open box with an arrow leaving the top-right corner.
 * Marks each Top-Websites row as a link that opens in a new tab. */
const ExternalLinkIcon = ({ color }: { color: string }): React.ReactElement => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 5h5v5" />
    <path d="M19 5l-7 7" />
    <path d="M18 13.5v4A1.5 1.5 0 0 1 16.5 19h-10A1.5 1.5 0 0 1 5 17.5v-10A1.5 1.5 0 0 1 6.5 6h4" />
  </svg>
);

/**
 * Running campaign detail. Data-driven template shared by the acquisition
 * "Running Campaign" and operations "Running Campaign" pages. A green-glow hero
 * (the campaign is live) over the shared `KpiSection` and a grid of creative
 * assets, each shown as a schematic preview of its ad format.
 */
const RunningCampaign = ({
  data,
  status = 'running',
  kind = 'campaign',
  onPause = noop,
  onResume = noop,
  loading,
  performanceLocked = false,
  styleText = '',
  className = '',
}: RunningCampaignProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hero, performance, assets, topWebsites } = data;

  // The page title, hero stat labels, ad-format type and channel names are a
  // fixed closed UI vocabulary reaching us as plain strings (from the shared
  // mock payloads). Localise them here at the render site, keyed off the stable
  // English value (which a paused dispatcher may already have translated; that
  // value simply falls through the map untouched), so the chrome stays reactive.
  const TITLE_LABELS: Record<string, string> = {
    'Running Campaign Details': t('Running Campaign Details'),
    'Paused Campaign Details': t('Paused Campaign Details'),
    'Ended Campaign Details': t('Ended Campaign Details'),
    'Running Promotion Details': t('Running Promotion Details'),
    'Paused Promotion Details': t('Paused Promotion Details'),
    'Ended Promotion Details': t('Ended Promotion Details'),
  };
  const HERO_LABELS: Record<string, string> = {
    'Ad Spend': t('Ad Spend'),
    'Players engaged': t('Players engaged'),
    Offering: t('Offering'),
    Duration: t('Duration'),
  };
  const ASSET_TYPE_LABELS: Record<string, string> = {
    Banner: t('Banner'),
    Display: t('Display'),
    Interstitial: t('Interstitial'),
  };
  const ASSET_CHANNEL_LABELS: Record<string, string> = {
    Web: t('Web'),
    Crypto: t('Crypto'),
    Apps: t('Apps'),
  };
  const titleLabel = (value: string): string => TITLE_LABELS[value] ?? value;
  const heroLabel = (value: string): string => HERO_LABELS[value] ?? value;
  const assetTypeLabel = (value: string): string => ASSET_TYPE_LABELS[value] ?? value;
  const channelsText = (channels: string[]): string =>
    channels.map((channel) => ASSET_CHANNEL_LABELS[channel] ?? channel).join(', ');

  // Loading per group. The wired acquisition path supplies real per-section flags via
  // `loading`; without it (operations promotions, still mock) each group fills in on its
  // own randomised mock latency. Campaign identity (title, image, controls) and the
  // asset previews are shown immediately.
  const mockHeroEp = useMockEndpoint(hero);
  const mockPerfEp = useMockEndpoint(performance.kpis);
  const mockSitesEp = useMockEndpoint(topWebsites?.items);
  const adSpendLoading = loading ? loading.heroAdSpend : mockHeroEp.loading;
  const perfLoading = loading ? loading.performance : mockPerfEp.loading;
  const sitesLoading = loading ? loading.topWebsites : mockSitesEp.loading;
  // On the wired path the offering (the campaign's promotion headline) and the duration
  // (the campaign period, known on resolve) need no fetch, so they show immediately; the
  // mock path keeps them on the cosmetic hero latency.
  const heroFiguresLoading = loading ? false : mockHeroEp.loading;
  // Clicking a banner opens it full-size in the shared modal Overlay. Only real banner
  // images are zoomable (the schematic wireframe fallback is not).
  const [zoomed, setZoomed] = React.useState<CampaignAsset | null>(null);

  // `hero.endedOn` is the ended payload's backstop signal; `status` is the
  // authoritative state from the dispatcher.
  const ended = status === 'ended' || Boolean(hero.endedOn);
  const paused = !ended && status === 'paused';
  const isPromotion = kind === 'promotion';
  // Hero glow: green for a live (running) campaign, grey for a paused/resumable one,
  // none for an ended one (flat dark surface).
  const glow: HeroGlow | undefined = ended ? undefined : paused ? 'paused' : 'live';

  // Pausing/resuming is a build-ahead control (writes a local status override, not a backed
  // API action yet), so the Pause/Resume button ships hidden behind the `campaign-pause`
  // feature flag — no veil, just absent — while the hero still reflects whatever status
  // (running / paused / ended) the data reports. Revealed live for QA via `show_in_progress`.
  const { isLocked } = useFeatureGate();
  const statusControlLocked = isLocked('campaign-pause');
  // The Pause/Resume button shows only for a live/paused campaign when the control is unlocked;
  // an ended campaign always shows its (non-interactive) "Ended on" tag.
  const showStatusControl = !ended && !statusControlLocked;
  // Suppress the whole controls row when it would be empty (e.g. the acquisition campaign detail
  // has no cross-link), so hiding the button leaves no orphaned padding band.
  const showControls = ended || Boolean(hero.crosslink) || showStatusControl;

  // Light-sheen feedback: bump a key each time the campaign flips between running
  // and paused so a fresh (keyed) `Sheen` mounts and sweeps once across the hero
  // card. A ref-tracked previous value keeps it from firing on the initial mount
  // (or when landing directly on an already-paused campaign).
  const [sheenTick, setSheenTick] = React.useState(0);
  const prevPaused = React.useRef(paused);
  React.useEffect(() => {
    if (prevPaused.current !== paused) {
      prevPaused.current = paused;
      setSheenTick((t) => t + 1);
    }
  }, [paused]);

  // Cross-link to the linked Acquisition campaign detail (operations promotions
  // that are "also in use for Acquisition"). The destination is resolved by the
  // dispatcher and carried on `hero.crosslink.to`.
  const crosslinkTo = hero.crosslink?.to;
  const onCrosslink = React.useCallback(() => {
    if (crosslinkTo) navigate(crosslinkTo);
  }, [navigate, crosslinkTo]);

  const onLoadMore = React.useCallback(() => {
    console.log('oh.module.running-campaign[loadMoreAssets]');
  }, []);

  return (
    <PageShell styleText={styleText} className={className}>
      <BackHeader title={titleLabel(data.header.title)} />

      <Hero>
        <HeroCard $glow={glow}>
          <HeroMain>
            <HeroThumb $src={hero.image} />
            <HeroTitles>
              <HeroTitle text={hero.title} tag="span" />
              <HeroSubtitle text={hero.subtitle} tag="span" />
            </HeroTitles>
          </HeroMain>
          <HeroOffering>
            <AdSpend>
              <AdSpendValue>
                {adSpendLoading ? <Skeleton width="5ch" height="0.78em" radius={6} /> : hero.adSpend.value}
              </AdSpendValue>
              <AdSpendLabel text={heroLabel(hero.adSpend.label)} tag="span" />
            </AdSpend>
            <OfferingList>
              <OfferingGroup>
                <OfferingLabel text={heroLabel(hero.offering.label)} tag="span" />
                {heroFiguresLoading ? <Skeleton width={64} height={13} radius={5} /> : <OfferingValue text={hero.offering.value} tag="span" />}
              </OfferingGroup>
              <OfferingGroup>
                <OfferingLabel text={heroLabel(hero.duration.label)} tag="span" />
                {heroFiguresLoading ? <Skeleton width={64} height={13} radius={5} /> : <OfferingValue text={hero.duration.value} tag="span" />}
              </OfferingGroup>
            </OfferingList>
          </HeroOffering>
          {!ended && sheenTick > 0 ? <Sheen key={sheenTick} aria-hidden="true" /> : null}
        </HeroCard>
        {showControls ? (
          <HeroControls>
            {ended ? (
              <EndedTag>
                <Icon name="stop" size={16} color={COLORS.icon} />
                <EndedLabel text="Ended on" tag="span" />
                <EndedLabel text={hero.endedOn ?? ''} tag="span" />
              </EndedTag>
            ) : (
              <>
                {hero.crosslink ? (
                  <CrossLink type="button" onClick={onCrosslink}>
                    <CrossLinkLabel text={hero.crosslink.label} tag="span" />
                    <Icon name="arrow-right-circle" size={24} color={COLORS.iconBright} />
                  </CrossLink>
                ) : null}
                {showStatusControl ? (
                  paused ? (
                    <ResumeButton type="button" onClick={onResume}>
                      <PlayIcon color={COLORS.ctaSolidText} />
                      <ResumeLabel text={isPromotion ? t('Resume Promotion') : t('Resume Campaign')} tag="span" />
                    </ResumeButton>
                  ) : (
                    <PauseButton type="button" onClick={onPause}>
                      <Icon name="pause" size={16} color={COLORS.pauseFg} />
                      <PauseLabel text={isPromotion ? t('Pause Promotion') : t('Pause Campaign')} tag="span" />
                    </PauseButton>
                  )
                ) : null}
              </>
            )}
          </HeroControls>
        ) : null}
      </Hero>

      {performanceLocked ? (
        // No API source for these figures yet: keep the section title, and frost EACH KPI
        // card individually. The veil re-shows the card's metric name in its own header
        // (over the frosted figures) so owners can read which metrics are coming; the mock
        // figures underneath show through when `show_in_progress` reveals the feature.
        // pad/fontSize match KpiTile's header (title at top:10 · left:15, 18px/700).
        <PerfSection>
          <SectionTitle title={t('Campaign Performance')} />
          <KpiGrid>
            {performance.kpis.map((kpi) => (
              <TileLock key={kpi.id}>
                <KpiTile kpi={kpi} loading={perfLoading} />
                <LockVeil name={kpi.title} fontSize={18} pad="10px 15px" radius={16} lockSize={20} />
              </TileLock>
            ))}
          </KpiGrid>
        </PerfSection>
      ) : (
        <KpiSection title={t('Campaign Performance')} kpis={performance.kpis} loading={perfLoading} />
      )}

      {topWebsites ? (
        <WebsitesSection>
          <SectionTitle title={t('Top 10 Websites')} />
          <WebsitesPanel>
            {topWebsites.items.length > 0 ? (
              <WebsitesList>
                {topWebsites.items.map((site, i) => (
                  <WebsiteRow
                    key={site.id}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('Open {{name}} in a new tab', { name: site.name })}
                  >
                    <Rank text={String(i + 1)} tag="span" />
                    <RowLine>
                      <WebsiteName text={site.name} tag="span" />
                      <Bullet aria-hidden="true" />
                      {sitesLoading ? (
                        <Skeleton width={96} height={13} radius={5} />
                      ) : (
                        <WebsiteValue
                          text="{{impressions}} impressions · {{clicks}} clicks"
                          placeholders={{ impressions: site.impressions, clicks: site.clicks }}
                          tag="span"
                        />
                      )}
                      <ExternalLinkIcon color={COLORS.textMuted} />
                    </RowLine>
                  </WebsiteRow>
                ))}
              </WebsitesList>
            ) : sitesLoading ? (
              <WebsitesList>
                {[0, 1, 2].map((k) => (
                  <WebsiteRow as="div" key={k}>
                    <Rank text={String(k + 1)} tag="span" />
                    <RowLine>
                      <Skeleton width={140} height={13} radius={5} />
                      <Bullet aria-hidden="true" />
                      <Skeleton width={96} height={13} radius={5} />
                    </RowLine>
                  </WebsiteRow>
                ))}
              </WebsitesList>
            ) : (
              <WebsitesEmpty text="No website traffic yet" tag="span" />
            )}
          </WebsitesPanel>
        </WebsitesSection>
      ) : null}

      {assets ? (
        <AssetsSection>
          <SectionTitle title={t('Campaigns Assets')} />
          <AssetsPanel>
            <AssetGrid>
              {assets.items.map((asset) => (
                <AssetCard key={asset.id}>
                  <AssetThumb
                    {...(asset.image ? { as: 'button', type: 'button', onClick: () => setZoomed(asset) } : {})}
                    $from={ASSET_PREVIEW[asset.tone].from}
                    $clickable={Boolean(asset.image)}
                    aria-label={asset.image ? t('Open {{name}}', { name: asset.name }) : undefined}
                  >
                    <AssetPreview asset={asset} />
                    <DimBadge text={`${asset.width}×${asset.height}`} tag="span" />
                  </AssetThumb>
                  <AssetInfo>
                    <AssetName text={asset.name} tag="span" />
                    <AssetMetaLine>
                      <AssetMetaType
                        text="{{type}} · {{dim}} ·"
                        placeholders={{ type: assetTypeLabel(asset.type), dim: `${asset.width}×${asset.height}` }}
                        tag="span"
                      />
                      <AssetMetaChannels
                        text={channelsText(asset.channels)}
                        $color={channelColor(asset.channels)}
                        tag="span"
                      />
                    </AssetMetaLine>
                  </AssetInfo>
                </AssetCard>
              ))}
            </AssetGrid>
            {assets.hasMore ? <LoadMore label={t('Load more campaign assets')} onClick={onLoadMore} /> : null}
          </AssetsPanel>
        </AssetsSection>
      ) : null}

      {zoomed ? (
        <Sheet title={zoomed.name} onClose={() => setZoomed(null)} maxHeight="90%">
          <ZoomImg src={zoomed.image} alt={zoomed.name} />
          <ZoomMeta
            text="{{type}} · {{dim}} · {{channels}}"
            placeholders={{
              type: assetTypeLabel(zoomed.type),
              dim: `${zoomed.width}×${zoomed.height}`,
              channels: channelsText(zoomed.channels),
            }}
            tag="span"
          />
        </Sheet>
      ) : null}
    </PageShell>
  );
};

export default RunningCampaign;
